<template>
  <div class="portal-home-dashboard pa-4 pa-md-6">
    <portal-page-header
      home
      title="Portal del paciente"
      subtitle="Bienvenido a su portal de salud"
    />

    <section v-if="portalTilesSkeleton" class="mb-10">
      <div
        class="portal-dashboard-tiles-grid portal-dashboard-tiles-grid--skeleton"
      >
        <v-skeleton-loader
          v-for="n in 4"
          :key="n"
          type="image"
          class="rounded-xl portal-dashboard-tile-skeleton"
          height="160"
        />
      </div>
    </section>

    <section
      v-else-if="menuError"
      class="mb-10 d-flex align-center justify-center text-body-2 text-medium-emphasis py-10 px-2"
    >
      No hay módulos disponibles.
    </section>

    <section v-else class="mb-4">
      <div class="portal-dashboard-tiles-grid">
        <v-card
          v-for="(tile, tIdx) in menuTileItems"
          :key="tileKey(tile, tIdx)"
          class="portal-tile rounded-xl overflow-hidden h-100 d-flex flex-column"
          elevation="3"
          :style="tileStyle(tile)"
          @click="openTile(tile)"
        >
          <v-card-text class="pa-0 text-white d-flex flex-column flex-grow-1">
            <div class="px-4 pt-4 pb-2 flex-grow-1 d-flex flex-column">
              <div class="d-flex justify-space-between align-start mb-3">
                <v-avatar size="44" class="portal-tile-icon-avatar flex-shrink-0">
                  <v-icon
                    :icon="portalModuleIcon(tile.path)"
                    size="28"
                    color="white"
                  />
                </v-avatar>
                <div
                  v-if="tileBadgeCount(tile) > 0"
                  class="portal-tile-icon-avatar portal-tile-counter-badge d-inline-flex align-center justify-center flex-shrink-0"
                >
                  <span class="text-h6 font-weight-bold text-white lh-1">
                    {{ tileBadgeCount(tile) }}
                  </span>
                </div>
              </div>
              <div class="text-h7 font-weight-bold">
                {{ tile.tileTitle || tile.title }}
              </div>
            </div>

            <v-divider
              color="white"
              class="portal-tile-divider opacity-90"
              thickness="1"
            />

            <div
              class="d-flex align-center justify-space-between pa-3 text-body-2"
            >
              <span class="text-truncate me-2">{{
                tileDescription(tile)
              }}</span>
              <v-icon
                :icon="chevronRightIcon"
                size="22"
                class="flex-shrink-0"
              />
            </div>
          </v-card-text>
        </v-card>
      </div>
    </section>

    <!-- Escritorio: tarjetas lado a lado -->
    <v-row
      v-show="lgAndUp"
      dense
      class="mt-4 portal-home-dashboard__bottom-row"
    >
      <v-col cols="12" lg="6">
        <v-card
          elevation="1"
          rounded="lg"
          class="portal-home-dashboard__section-card-elevated portal-home-dashboard__turnos-card bg-surface d-flex flex-column align-self-start"
        >
          <v-card-title
            class="py-2 px-3 text-subtitle-2 font-weight-bold portal-home-dashboard__section-title-tonal d-flex align-center"
          >
            <span class="d-inline-flex align-center flex-grow-1 min-w-0 gap-2">
              <span class="text-wrap">Próximos turnos</span>
              <v-badge
                v-if="resumenCantTurnos > 0"
                :content="String(resumenCantTurnos)"
                color="primary"
                inline
              />
            </span>
          </v-card-title>
          <v-divider />
          <portal-turnos-listado-seccion
            variant="home"
            :items="turnosPendientesHomeItems"
            :loading="turnosPendientes.loading"
            :error="turnosPendientes.error"
            permite-cancelar
          />
        </v-card>
      </v-col>
      <v-col cols="12" lg="6">
        <portal-home-ultimos-informes-card v-if="lgAndUp" />
      </v-col>
    </v-row>

    <!-- Móvil / tablet: acordeón colapsado por defecto, chevron en cabecera -->
    <v-expansion-panels
      v-show="!lgAndUp"
      v-model="mobileBottomPanels"
      multiple
      flat
      :expand-icon="expandPanelIcon"
      :collapse-icon="collapsePanelIcon"
      class="mt-4 portal-home-dashboard__mobile-panels bg-transparent"
    >
      <v-expansion-panel
        rounded="lg"
        class="portal-home-dashboard__section-card-elevated bg-surface"
      >
        <v-expansion-panel-title
          class="portal-home-dashboard__expansion-title portal-home-dashboard__section-title-tonal py-3 px-3 text-subtitle-2 font-weight-bold d-flex align-center"
        >
          <span class="d-inline-flex align-center flex-grow-1 min-w-0 gap-2">
            <span class="text-wrap">Próximos turnos</span>
            <v-badge
              v-if="resumenCantTurnos > 0"
              :content="String(resumenCantTurnos)"
              color="primary"
              inline
            />
          </span>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="pa-0">
          <portal-turnos-listado-seccion
            variant="home"
            :items="turnosPendientesHomeItems"
            :loading="turnosPendientes.loading"
            :error="turnosPendientes.error"
            permite-cancelar
          />
        </v-expansion-panel-text>
      </v-expansion-panel>
      <v-expansion-panel
        eager
        rounded="lg"
        class="portal-home-dashboard__section-card-elevated bg-surface"
      >
        <v-expansion-panel-title
          class="portal-home-dashboard__expansion-title portal-home-dashboard__section-title-tonal portal-home-dashboard__informes-expansion-title py-3 px-3 text-subtitle-2 font-weight-bold d-flex align-center"
        >
          <span class="d-inline-flex align-center flex-grow-1 min-w-0 gap-2">
            <span class="text-wrap">Últimos informes</span>
            <v-badge
              v-if="informesUltimosTablaCount > 0"
              :content="String(informesUltimosTablaCount)"
              color="primary"
              inline
            />
          </span>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="pa-0">
          <portal-home-ultimos-informes-card
            v-if="!lgAndUp"
            embedded
          />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

import icons, { PORTAL_MODULE_MENU_ICONS } from "../../../utils/enums/icons";
import { normalizePortalModulePath } from "../../../utils/helpers/normalizePortalModulePath";
import { turnoVisibleParaPortalActivo } from "../../../utils/helpers/portalTurnosVisibility";

import PortalHomeUltimosInformesCard from "./PortalHomeUltimosInformesCard.vue";
import PortalTurnosListadoSeccion from "../turnos/PortalTurnosListadoSeccion.vue";
import PortalPageHeader from "../../../components/layout/PortalPageHeader.vue";

export default {
  name: "PortalHomeDashboard",
  components: {
    PortalPageHeader,
    PortalHomeUltimosInformesCard,
    PortalTurnosListadoSeccion,
  },
  inject: {
    portalHomeLayout: { default: null },
  },
  data() {
    return {
      calendarSectionIcon: icons.CALENDAR,
      chevronRightIcon: icons.CHEVRON_RIGHT,
      expandPanelIcon: icons.CHEVRON_DOWN,
      collapsePanelIcon: icons.CHEVRON_UP,
      mobileBottomPanels: [],
      windowWidth:
        typeof window !== "undefined" ? window.innerWidth : 1280,
      homeInitialInfoReady: false,
    };
  },
  computed: {
    ...mapState("portalHome", [
      "homeInitialInfo",
      "informesHome",
      "turnosPendientes",
    ]),
    menuTileItems() {
      return this.portalHomeLayout?.menuTileItems ?? [];
    },
    menuLoading() {
      return this.portalHomeLayout?.menuLoading ?? false;
    },
    menuError() {
      return this.portalHomeLayout?.menuError ?? null;
    },
    vinculosFamiliares() {
      return this.portalHomeLayout?.vinculosFamiliares ?? [];
    },
    vinculosFamiliaresCargados() {
      return this.portalHomeLayout?.vinculosFamiliaresCargados === true;
    },
    visualizandoFamiliar() {
      return this.portalHomeLayout?.visualizandoFamiliar === true;
    },
    portalTilesSkeleton() {
      if (this.menuLoading) return true;
      if (this.menuError) return false;
      return !this.homeInitialInfoReady || !this.vinculosFamiliaresCargados;
    },
    lgAndUp() {
      return this.windowWidth >= 1280;
    },
    /**
     * Próximos turnos del portal activo. Desde el portal titular se conservan
     * turnos propios y de vínculos pendientes; los de vínculos confirmados se
     * consultan navegando al portal de ese familiar.
     */
    turnosPendientesHomeItems() {
      const items = this.turnosPendientes?.items;
      if (!Array.isArray(items)) return [];
      return items
        .filter((item) => !item.cancelado && this.turnoVisibleEnProximos(item))
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
          const fa = Number(a.item?.fechaOrdenMs)
          const fb = Number(b.item?.fechaOrdenMs)
          const va = Number.isFinite(fa) ? fa : Number.MAX_SAFE_INTEGER
          const vb = Number.isFinite(fb) ? fb : Number.MAX_SAFE_INTEGER
          return va - vb || a.index - b.index
        })
        .map((x) => x.item);
    },
    /** Contador del badge y del tile Mis turnos (misma lista que el bloque del home). */
    resumenCantTurnos() {
      const tp = this.turnosPendientes;
      if (!tp?.fetched || tp.loading) return 0;
      return this.turnosPendientesHomeItems.length;
    },
    informesUltimosTablaCount() {
      const items = this.informesHome?.items;
      return Array.isArray(items) ? items.length : 0;
    },
  },
  async mounted() {
    this.windowWidth = window.innerWidth;
    window.addEventListener("resize", this.onWindowResize, { passive: true });
    this.homeInitialInfoReady = false;
    try {
      await Promise.all([
        this.fetchHomeInitialInfo(),
        this.fetchTurnosPendientes(),
      ]);
    } finally {
      this.homeInitialInfoReady = true;
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onWindowResize);
  },
  methods: {
    ...mapActions({
      fetchHomeInitialInfo: 'portalHome/fetchHomeInitialInfo',
      fetchTurnosPendientes: 'portalHome/fetchTurnosPendientes',
    }),
    onWindowResize() {
      this.windowWidth = window.innerWidth;
    },
    turnoVisibleEnProximos(turno) {
      return turnoVisibleParaPortalActivo(turno, {
        visualizandoFamiliar: this.visualizandoFamiliar,
        vinculosFamiliares: this.vinculosFamiliares,
      });
    },
    portalModuleIcon(path) {
      const k = String(path ?? "")
        .trim()
        .replace(/^\/+/, "")
        .toLowerCase();
      return PORTAL_MODULE_MENU_ICONS[k] ?? icons.VIEW_DASHBOARD;
    },
    tileKey(tile, idx) {
      return (
        tile.id ??
        tile.path ??
        tile.tileTitle ??
        `t-${idx}`
      );
    },
    tileDescription(tile) {
      const d =
        tile.tileDescription ??
        tile.tileCompleteDescription;
      return d != null && String(d).trim() !== "" ? String(d).trim() : "\u2014";
    },
    tileStyle(tile) {
      const c = tile.color;
      const base = c && String(c).trim() ? String(c).trim() : null;
      return {
        "--portal-tile-color": base || "var(--portal-primary)",
        background: "var(--portal-tile-bg, var(--portal-tile-color))",
        cursor: "pointer",
      };
    },
    async openTile(tile) {
      const p = tile.path;
      const layout = this.portalHomeLayout;
      if (layout && typeof layout.navigateToModulePath === "function") {
        await layout.navigateToModulePath(p);
        return;
      }
      const target = normalizePortalModulePath(p);
      if (!target || target === "/") return;
      this.$router.push({ path: target });
    },
    tilePathSlug(tile) {
      const raw = tile.path ?? "";
      const norm = normalizePortalModulePath(raw)
        .replace(/^\/+/, "")
        .toLowerCase();
      const first = norm.split("/").filter(Boolean)[0] ?? "";
      return first;
    },
    tileBadgeCount(tile) {
      const info = this.homeInitialInfo;
      if (!info) return 0;
      const slug = this.tilePathSlug(tile);
      if (slug === "appointments") {
        return this.resumenCantTurnos;
      }
      if (slug === "diagimgreports") {
        return Number(info.cantInformesDiagImg ?? 0) || 0;
      }
      if (slug === "labreports" || slug === "externallabreports") {
        return Number(info.cantInformesLab ?? 0) || 0;
      }
      if (slug === "otherdocuments") {
        return Number(info.otrosDocumentos ?? 0) || 0;
      }
      if (slug === "admissions") {
        return Number(info.cantEpicrisis ?? 0) || 0;
      }
      return 0;
    },
  },
};
</script>

<style scoped>
.portal-dashboard-tiles-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  overflow-x: visible;
  padding-bottom: 4px;
}
@media (min-width: 1024px) {
  .portal-dashboard-tiles-grid {
    display: flex;
    flex-wrap: nowrap;
    align-items: stretch;
  }
  .portal-dashboard-tiles-grid > * {
    flex: 1 1 0;
    min-width: 0;
  }
}
.portal-dashboard-tiles-grid--skeleton {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  overflow-x: visible;
}
/* type="image": un solo bloque; Vuetify fija border-radius 0 en __image */
.portal-dashboard-tile-skeleton :deep(.v-skeleton-loader__image) {
  height: 160px;
  border-radius: 12px;
  flex: 1 1 100%;
}
@media (min-width: 1024px) {
  .portal-dashboard-tiles-grid--skeleton {
    display: flex;
    flex-wrap: nowrap;
    align-items: stretch;
  }
  .portal-dashboard-tiles-grid--skeleton > * {
    flex: 1 1 0;
    min-width: 0;
  }
}
.portal-tile {
  --portal-tile-bg: var(--portal-tile-color, var(--portal-primary));
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  min-height: 158px;
}
.portal-dark-mode .portal-tile {
  --portal-tile-bg: color-mix(
    in srgb,
    var(--portal-tile-color, var(--portal-primary)) 58%,
    #101820
  );
  border: 1px solid rgba(255, 255, 255, 0.12);
}
.portal-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}
.portal-dark-mode .portal-tile:hover {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.38) !important;
}
.portal-tile-icon-avatar {
  background: rgba(255, 255, 255, 0.22) !important;
  border-radius: 35% !important;
}
.portal-tile-counter-badge {
  min-height: 44px;
  min-width: 44px;
  padding: 0 8px;
}
.portal-tile-divider {
  opacity: 0.55 !important;
}
.portal-home-dashboard__bottom-row {
  align-items: flex-start;
}
.portal-home-dashboard__turnos-card {
  width: 100%;
}
.portal-home-dashboard__section-card-elevated {
  border: 1px solid #eceff1;
}
.portal-dark-mode .portal-home-dashboard__section-card-elevated {
  border-color: rgba(255, 255, 255, 0.1);
}
.portal-home-dashboard__section-title-tonal {
  color: rgb(var(--v-theme-primary)) !important;
  line-height: 1.35;
  background-color: rgba(var(--v-theme-primary), 0.12) !important;
  font-size: 20px !important;
  font-weight: bold !important;
}
.portal-home-dashboard__mobile-panels {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.portal-home-dashboard__mobile-panels :deep(.v-expansion-panel) {
  border: 1px solid #eceff1;
  box-shadow: none !important;
}
.portal-dark-mode
  .portal-home-dashboard__mobile-panels
  :deep(.v-expansion-panel) {
  border-color: rgba(255, 255, 255, 0.1);
}
.portal-home-dashboard__mobile-panels :deep(.v-expansion-panel::before) {
  box-shadow: none !important;
}
.portal-home-dashboard__mobile-panels :deep(.v-expansion-panel-text) {
  padding: 0 !important;
}
.portal-home-dashboard__mobile-panels :deep(.v-expansion-panel-text__wrapper) {
  padding: 0 !important;
}
.portal-home-dashboard__expansion-title :deep(.v-expansion-panel-title__overlay) {
  border-radius: inherit;
}
.portal-home-dashboard__informes-expansion-title :deep(.v-expansion-panel-title__icon) {
  margin-inline-start: 0;
}
</style>
